import React from "react";
import { Link, useLocation } from "react-router-dom";
import { LOGO_URL } from "../../lib/api";
import { useContent } from "../../context/ContentContext";

function LegalLink({ l }) {
  const raw = (l.href || "").trim();
  const { pathname } = useLocation();
  const cls = "label-mono text-peca-grey hover:text-peca-yellow transition-colors duration-200";
  if (raw.startsWith("#")) {
    const resolved = pathname === "/" ? raw : `/${raw}`;
    return <a href={resolved} data-testid={`footer-link-${l.label}`} className={cls}>{l.label}</a>;
  }
  if (/^https?:\/\//i.test(raw)) {
    return <a href={raw} target="_blank" rel="noopener noreferrer" data-testid={`footer-link-${l.label}`} className={cls}>{l.label}</a>;
  }
  const to = raw.startsWith("/") ? raw : `/${raw}`;
  return <Link to={to} data-testid={`footer-link-${l.label}`} className={cls}>{l.label}</Link>;
}

export default function Footer({ content }) {
  const f = content?.footer || {};
  const brand = content?.brand || {};
  const { locale } = useContent();
  const L = locale === "en" ? { contact: "Contact", socials: "Social" } : { contact: "Contacto", socials: "Redes" };

  return (
    <footer data-testid="site-footer" className="bg-peca-black border-t border-peca-line">
      <div className="mx-auto max-w-[1400px] px-5 md:px-10 py-16">
        <div className="grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-6">
            <img src={LOGO_URL} alt="Peça Business Solutions" className="h-14 md:h-16 w-auto mb-8" />
            <p className="font-display font-extrabold uppercase text-2xl md:text-4xl tracking-tight text-peca-cream max-w-lg leading-[1]">
              {f.tagline}
            </p>
          </div>

          <div className="md:col-span-3 md:col-start-8">
            <span className="label-mono text-peca-grey">{L.contact}</span>
            <a href={`mailto:${f.email}`} className="mt-4 block text-peca-cream hover:text-peca-yellow transition-colors duration-200 break-all">
              {f.email}
            </a>
          </div>

          <div className="md:col-span-2">
            <span className="label-mono text-peca-grey">{L.socials}</span>
            <div className="mt-4 flex flex-col gap-2">
              {(f.socials || []).map((s) => (
                <a key={s.label} href={s.href} target="_blank" rel="noopener noreferrer" className="text-peca-cream hover:text-peca-yellow transition-colors duration-200">
                  {s.label} ↗
                </a>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-16 pt-8 border-t border-peca-line flex flex-col gap-6">
          <div className="flex flex-wrap items-center gap-x-6 gap-y-3" data-testid="footer-legal-links">
            {(f.legalLinks || []).map((l, i) => (
              <LegalLink key={`${l.href}-${i}`} l={l} />
            ))}
          </div>
          <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
            <span className="label-mono text-peca-grey">{brand.name || "PEÇA"} — {brand.suffix || "Business Solutions"}</span>
            <div className="flex items-center gap-3">
              <span className="label-mono text-peca-grey">{f.copyright}</span>
              <Link
                to="/portal"
                data-testid="admin-portal-link"
                aria-label="Portal"
                title="✳"
                className="text-peca-line hover:text-peca-yellow transition-colors duration-200 text-lg leading-none select-none"
              >
                ✳
              </Link>
            </div>
          </div>
        </div>
      </div>
    </footer>
  );
}
